/**
 * pdfI18n.js
 * ---------------------------------------------------------------
 * Central i18n system dedicated EXCLUSIVELY to the PDF export.
 *
 * Design rules (enforced by this module):
 *   1. When a language is selected for the PDF, that language is the
 *      SOVEREIGN language of the whole document. No key is silently
 *      resolved through another language.
 *   2. Every visible text in the PDF templates comes from
 *      `pdfTranslations[lang]` — no hardcoded strings in templates.
 *   3. A missing key never falls back to English. It logs
 *      `MISSING_PDF_TRANSLATION` and returns a `‹key›` sentinel.
 *   4. Numbers, currency, percentages and dates are formatted with
 *      Intl using the locale mapped from the PDF language.
 *   5. Pluralised nouns (sets, doors) use Intl.PluralRules with
 *      per-language `<key>_one` / `<key>_other` variants.
 * ---------------------------------------------------------------
 */

// ---------------------------------------------------------------
// Language → BCP-47 locale
// ---------------------------------------------------------------
export const langToLocale = {
  "pt-PT": "pt-PT",
  "pt-BR": "pt-BR",
  es: "es-ES",
  de: "de-DE",
  en: "en-GB",
  it: "it-IT",
};

export const SUPPORTED_PDF_LANGS = Object.keys(langToLocale);

// ---------------------------------------------------------------
// PDF translations dictionary
// ---------------------------------------------------------------
//  * Every language MUST have every key listed in REQUIRED_PDF_KEYS.
//  * Placeholders inside strings use `{name}` syntax.
//  * Pluralisable nouns MUST provide `_one` and `_other` variants
//    (Intl.PluralRules 'one' and 'other' categories cover all six
//    languages we support here).
// ---------------------------------------------------------------
export const pdfTranslations = {
  "pt-PT": {
    // --- Header ---
    headerTitle: "Análise Técnica de Custos",
    headerSubtitle: "Vedação de portas traseiras · relatório de poupança",
    execTitle: "Resumo Executivo",
    technicalDetail: "Detalhe Técnico",
    refLabel: "REF",
    brandLine: "PRIMO",
    // --- Sections ---
    s1: "§1 Dados do cliente e reboque",
    s2: "§2 Configuração das portas",
    s3: "§3 Decomposição de custos",
    s4: "§4 Tempo de mão-de-obra",
    s5: "§5 Resumo de poupança",
    // --- Client / trailer fields ---
    client: "Cliente",
    contact: "Contacto",
    email: "Email",
    production: "Produção",
    doorHeight: "Altura da porta",
    doorWidth: "Largura da porta",
    doorsPerTrailer: "Portas / reboque",
    profile: "Perfil",
    notes: "Notas",
    // --- Door layout ---
    leftDoor: "Porta Esquerda",
    rightDoor: "Porta Direita",
    leftDoorDesc:
      "Vedação {t}mm nas 3 extremidades exteriores + perfil central {t}mm na extremidade interior.",
    rightDoorDesc:
      "Vedação {t}mm em todo o perímetro. Sela contra o perfil central da porta esquerda.",
    dsLength: "Comprimento vedação",
    cpLength: "Comprimento perfil central",
    // --- Table headers ---
    component: "Componente",
    customer: "Atual",
    primo: "Primo",
    savings: "Δ Poupança",
    operation: "Operação",
    // --- Cost rows ---
    leftDoorProfiles: "Porta Esquerda · vedação + perfil central",
    rightDoorProfiles: "Porta Direita · só vedação",
    cuttingWelding: "Corte / soldadura & corte",
    cornerGluing: "Colagem de cantos",
    mounting: "Montagem",
    steelbandRow: "Cinta metálica + furação + rebites",
    glueRow: "Cola / material",
    eliminated: "— eliminado",
    totalSet: "Total (por conjunto de {n} portas)",
    totalLabour: "Tempo total mão-de-obra",
    // --- Summary ---
    perSet: "Por conjunto ({n} portas)",
    annualised: "Anualizado",
    costReduction: "redução de custo",
    savedMoney: "poupado",
    savedHours: "poupadas",
    // --- Executive / visual ---
    execHeroLabel: "Poupança demonstrada",
    execPerDoors: "por {n} portas",
    execCompareTitle: "Sem Primo vs Com Primo",
    beforePrimo: "Sem Primo",
    afterPrimo: "Com Primo",
    cost: "Custo",
    time: "Tempo",
    costSavings: "Poupança em Custo",
    timeSavings: "Poupança em Tempo",
    projectionPeriod: "Projecção {period}",
    projectionAnnual: "Projecção Anualizada",
    fleetTotalSavings: "Poupança total na frota",
    // --- Period labels ---
    weeklyLabel: "Semanal",
    monthlyLabel: "Mensal",
    annualLabel: "Anual",
    weeklyLower: "semanal",
    monthlyLower: "mensal",
    annualLower: "anual",
    // --- Units and pluralised nouns ---
    perYear: "/ ano",
    doors_one: "porta",
    doors_other: "portas",
    sets_one: "conjunto",
    sets_other: "conjuntos",
    hours_one: "hora",
    hours_other: "horas",
    // --- Alt text ---
    logoAlt: "Primo",
    signatureAlt: "Assinatura",
  },

  "pt-BR": {
    headerTitle: "Análise Técnica de Custos",
    headerSubtitle: "Vedação de portas traseiras · relatório de economia",
    execTitle: "Resumo Executivo",
    technicalDetail: "Detalhe Técnico",
    refLabel: "REF",
    brandLine: "PRIMO",
    s1: "§1 Dados do cliente e reboque",
    s2: "§2 Configuração das portas",
    s3: "§3 Decomposição de custos",
    s4: "§4 Tempo de mão de obra",
    s5: "§5 Resumo de economia",
    client: "Cliente",
    contact: "Contato",
    email: "Email",
    production: "Produção",
    doorHeight: "Altura da porta",
    doorWidth: "Largura da porta",
    doorsPerTrailer: "Portas / reboque",
    profile: "Perfil",
    notes: "Notas",
    leftDoor: "Porta Esquerda",
    rightDoor: "Porta Direita",
    leftDoorDesc:
      "Vedação {t}mm nas 3 extremidades externas + perfil central {t}mm na borda interna.",
    rightDoorDesc:
      "Vedação {t}mm em todo o perímetro. Veda contra o perfil central da porta esquerda.",
    dsLength: "Comprimento vedação",
    cpLength: "Comprimento perfil central",
    component: "Componente",
    customer: "Atual",
    primo: "Primo",
    savings: "Δ Economia",
    operation: "Operação",
    leftDoorProfiles: "Porta Esquerda · vedação + perfil central",
    rightDoorProfiles: "Porta Direita · só vedação",
    cuttingWelding: "Corte / soldagem & corte",
    cornerGluing: "Colagem de cantos",
    mounting: "Montagem",
    steelbandRow: "Cinta de aço + furação + rebites",
    glueRow: "Cola / material",
    eliminated: "— eliminado",
    totalSet: "Total (por conjunto de {n} portas)",
    totalLabour: "Tempo total mão de obra",
    perSet: "Por conjunto ({n} portas)",
    annualised: "Anualizado",
    costReduction: "redução de custo",
    savedMoney: "economizado",
    savedHours: "economizadas",
    execHeroLabel: "Economia demonstrada",
    execPerDoors: "por {n} portas",
    execCompareTitle: "Sem Primo vs Com Primo",
    beforePrimo: "Sem Primo",
    afterPrimo: "Com Primo",
    cost: "Custo",
    time: "Tempo",
    costSavings: "Economia em Custo",
    timeSavings: "Economia em Tempo",
    projectionPeriod: "Projeção {period}",
    projectionAnnual: "Projeção Anualizada",
    fleetTotalSavings: "Economia total na frota",
    weeklyLabel: "Semanal",
    monthlyLabel: "Mensal",
    annualLabel: "Anual",
    weeklyLower: "semanal",
    monthlyLower: "mensal",
    annualLower: "anual",
    perYear: "/ ano",
    doors_one: "porta",
    doors_other: "portas",
    sets_one: "conjunto",
    sets_other: "conjuntos",
    hours_one: "hora",
    hours_other: "horas",
    logoAlt: "Primo",
    signatureAlt: "Assinatura",
  },

  es: {
    headerTitle: "Análisis Técnico de Costes",
    headerSubtitle: "Sellado de puertas traseras · informe de ahorro",
    execTitle: "Resumen Ejecutivo",
    technicalDetail: "Detalle Técnico",
    refLabel: "REF",
    brandLine: "PRIMO",
    s1: "§1 Datos del cliente y remolque",
    s2: "§2 Configuración de las puertas",
    s3: "§3 Desglose de costes",
    s4: "§4 Tiempo de mano de obra",
    s5: "§5 Resumen de ahorro",
    client: "Cliente",
    contact: "Contacto",
    email: "Email",
    production: "Producción",
    doorHeight: "Altura de la puerta",
    doorWidth: "Ancho de la puerta",
    doorsPerTrailer: "Puertas / remolque",
    profile: "Perfil",
    notes: "Notas",
    leftDoor: "Puerta Izquierda",
    rightDoor: "Puerta Derecha",
    leftDoorDesc:
      "Sellado {t}mm en las 3 aristas exteriores + perfil central {t}mm en la arista interior.",
    rightDoorDesc:
      "Sellado {t}mm en todo el perímetro. Sella contra el perfil central de la puerta izquierda.",
    dsLength: "Longitud sellado",
    cpLength: "Longitud perfil central",
    component: "Componente",
    customer: "Actual",
    primo: "Primo",
    savings: "Δ Ahorro",
    operation: "Operación",
    leftDoorProfiles: "Puerta Izquierda · sellado + perfil central",
    rightDoorProfiles: "Puerta Derecha · sólo sellado",
    cuttingWelding: "Corte / soldadura & corte",
    cornerGluing: "Encolado de esquinas",
    mounting: "Montaje",
    steelbandRow: "Cinta metálica + perforación + remaches",
    glueRow: "Pegamento / material",
    eliminated: "— eliminado",
    totalSet: "Total (por conjunto de {n} puertas)",
    totalLabour: "Tiempo total mano de obra",
    perSet: "Por conjunto ({n} puertas)",
    annualised: "Anualizado",
    costReduction: "reducción de coste",
    savedMoney: "ahorrado",
    savedHours: "ahorradas",
    execHeroLabel: "Ahorro demostrado",
    execPerDoors: "por {n} puertas",
    execCompareTitle: "Sin Primo vs Con Primo",
    beforePrimo: "Sin Primo",
    afterPrimo: "Con Primo",
    cost: "Coste",
    time: "Tiempo",
    costSavings: "Ahorro en Coste",
    timeSavings: "Ahorro en Tiempo",
    projectionPeriod: "Proyección {period}",
    projectionAnnual: "Proyección Anualizada",
    fleetTotalSavings: "Ahorro total en la flota",
    weeklyLabel: "Semanal",
    monthlyLabel: "Mensual",
    annualLabel: "Anual",
    weeklyLower: "semanal",
    monthlyLower: "mensual",
    annualLower: "anual",
    perYear: "/ año",
    doors_one: "puerta",
    doors_other: "puertas",
    sets_one: "conjunto",
    sets_other: "conjuntos",
    hours_one: "hora",
    hours_other: "horas",
    logoAlt: "Primo",
    signatureAlt: "Firma",
  },

  de: {
    headerTitle: "Technische Kostenanalyse",
    headerSubtitle: "Hecktürdichtung · Einsparungsbericht",
    execTitle: "Executive Summary",
    technicalDetail: "Technisches Detail",
    refLabel: "REF",
    brandLine: "PRIMO",
    s1: "§1 Kunden- und Anhängerdaten",
    s2: "§2 Türkonfiguration",
    s3: "§3 Kostenaufstellung",
    s4: "§4 Arbeitszeit",
    s5: "§5 Einsparungsübersicht",
    client: "Kunde",
    contact: "Ansprechpartner",
    email: "E-Mail",
    production: "Produktion",
    doorHeight: "Türhöhe",
    doorWidth: "Türbreite",
    doorsPerTrailer: "Türen / Anhänger",
    profile: "Profil",
    notes: "Notizen",
    leftDoor: "Linke Tür",
    rightDoor: "Rechte Tür",
    leftDoorDesc:
      "Dichtung {t}mm an den 3 Außenkanten + Mittelprofil {t}mm an der Innenkante.",
    rightDoorDesc:
      "Dichtung {t}mm um den gesamten Umfang. Dichtet gegen das Mittelprofil der linken Tür.",
    dsLength: "Dichtungslänge",
    cpLength: "Länge Mittelprofil",
    component: "Komponente",
    customer: "Aktuell",
    primo: "Primo",
    savings: "Δ Einsparung",
    operation: "Vorgang",
    leftDoorProfiles: "Linke Tür · Dichtung + Mittelprofil",
    rightDoorProfiles: "Rechte Tür · nur Dichtung",
    cuttingWelding: "Schneiden / Schweißen & Schneiden",
    cornerGluing: "Eckverklebung",
    mounting: "Montage",
    steelbandRow: "Stahlband + Bohren + Nieten",
    glueRow: "Kleber / Material",
    eliminated: "— entfällt",
    totalSet: "Gesamt (pro Satz {n} Türen)",
    totalLabour: "Gesamte Arbeitszeit",
    perSet: "Pro Satz ({n} Türen)",
    annualised: "Jährlich",
    costReduction: "Kostenreduktion",
    savedMoney: "gespart",
    savedHours: "gespart",
    execHeroLabel: "Nachgewiesene Einsparung",
    execPerDoors: "pro {n} Türen",
    execCompareTitle: "Ohne Primo vs Mit Primo",
    beforePrimo: "Ohne Primo",
    afterPrimo: "Mit Primo",
    cost: "Kosten",
    time: "Zeit",
    costSavings: "Kostenersparnis",
    timeSavings: "Zeitersparnis",
    projectionPeriod: "Prognose {period}",
    projectionAnnual: "Jahresprognose",
    fleetTotalSavings: "Gesamteinsparung Flotte",
    weeklyLabel: "Wöchentlich",
    monthlyLabel: "Monatlich",
    annualLabel: "Jährlich",
    weeklyLower: "wöchentlich",
    monthlyLower: "monatlich",
    annualLower: "jährlich",
    perYear: "/ Jahr",
    doors_one: "Tür",
    doors_other: "Türen",
    sets_one: "Satz",
    sets_other: "Sätze",
    hours_one: "Stunde",
    hours_other: "Stunden",
    logoAlt: "Primo",
    signatureAlt: "Unterschrift",
  },

  en: {
    headerTitle: "Technical Cost Analysis",
    headerSubtitle: "Back-door gasket · savings report",
    execTitle: "Executive Summary",
    technicalDetail: "Technical Detail",
    refLabel: "REF",
    brandLine: "PRIMO",
    s1: "§1 Client & trailer specifications",
    s2: "§2 Door configuration",
    s3: "§3 Cost breakdown",
    s4: "§4 Labour time",
    s5: "§5 Savings summary",
    client: "Client",
    contact: "Contact",
    email: "Email",
    production: "Production",
    doorHeight: "Door height",
    doorWidth: "Door width",
    doorsPerTrailer: "Doors / trailer",
    profile: "Profile",
    notes: "Notes",
    leftDoor: "Left door",
    rightDoor: "Right door",
    leftDoorDesc:
      "{t}mm sealing on the 3 outer edges + {t}mm central profile on the inner edge.",
    rightDoorDesc:
      "{t}mm sealing all around the perimeter. Seals against the central profile of the left door.",
    dsLength: "Sealing length",
    cpLength: "Central profile length",
    component: "Component",
    customer: "Current",
    primo: "Primo",
    savings: "Δ Savings",
    operation: "Operation",
    leftDoorProfiles: "Left door · sealing + central profile",
    rightDoorProfiles: "Right door · sealing only",
    cuttingWelding: "Cutting / welding & cutting",
    cornerGluing: "Corner gluing",
    mounting: "Mounting",
    steelbandRow: "Steel band + drilling + rivets",
    glueRow: "Glue / material",
    eliminated: "— eliminated",
    totalSet: "Total (per set of {n} doors)",
    totalLabour: "Total labour time",
    perSet: "Per set ({n} doors)",
    annualised: "Annualised",
    costReduction: "cost reduction",
    savedMoney: "saved",
    savedHours: "saved",
    execHeroLabel: "Demonstrated savings",
    execPerDoors: "per {n} doors",
    execCompareTitle: "Without Primo vs With Primo",
    beforePrimo: "Without Primo",
    afterPrimo: "With Primo",
    cost: "Cost",
    time: "Time",
    costSavings: "Cost savings",
    timeSavings: "Time savings",
    projectionPeriod: "{period} projection",
    projectionAnnual: "Annualised projection",
    fleetTotalSavings: "Total fleet savings",
    weeklyLabel: "Weekly",
    monthlyLabel: "Monthly",
    annualLabel: "Annual",
    weeklyLower: "weekly",
    monthlyLower: "monthly",
    annualLower: "annual",
    perYear: "/ year",
    doors_one: "door",
    doors_other: "doors",
    sets_one: "set",
    sets_other: "sets",
    hours_one: "hour",
    hours_other: "hours",
    logoAlt: "Primo",
    signatureAlt: "Signature",
  },

  it: {
    headerTitle: "Analisi Tecnica dei Costi",
    headerSubtitle: "Guarnizione porta posteriore · rapporto di risparmio",
    execTitle: "Riepilogo Esecutivo",
    technicalDetail: "Dettaglio Tecnico",
    refLabel: "REF",
    brandLine: "PRIMO",
    s1: "§1 Dati cliente e rimorchio",
    s2: "§2 Configurazione porte",
    s3: "§3 Ripartizione dei costi",
    s4: "§4 Tempo di manodopera",
    s5: "§5 Riepilogo risparmio",
    client: "Cliente",
    contact: "Contatto",
    email: "Email",
    production: "Produzione",
    doorHeight: "Altezza porta",
    doorWidth: "Larghezza porta",
    doorsPerTrailer: "Porte / rimorchio",
    profile: "Profilo",
    notes: "Note",
    leftDoor: "Porta Sinistra",
    rightDoor: "Porta Destra",
    leftDoorDesc:
      "Guarnizione {t}mm sui 3 bordi esterni + profilo centrale {t}mm sul bordo interno.",
    rightDoorDesc:
      "Guarnizione {t}mm su tutto il perimetro. Sigilla contro il profilo centrale della porta sinistra.",
    dsLength: "Lunghezza guarnizione",
    cpLength: "Lunghezza profilo centrale",
    component: "Componente",
    customer: "Attuale",
    primo: "Primo",
    savings: "Δ Risparmio",
    operation: "Operazione",
    leftDoorProfiles: "Porta Sinistra · guarnizione + profilo centrale",
    rightDoorProfiles: "Porta Destra · solo guarnizione",
    cuttingWelding: "Taglio / saldatura & taglio",
    cornerGluing: "Incollaggio angoli",
    mounting: "Montaggio",
    steelbandRow: "Fascia metallica + foratura + rivetti",
    glueRow: "Colla / materiale",
    eliminated: "— eliminato",
    totalSet: "Totale (per set di {n} porte)",
    totalLabour: "Tempo totale manodopera",
    perSet: "Per set ({n} porte)",
    annualised: "Annualizzato",
    costReduction: "riduzione dei costi",
    savedMoney: "risparmiato",
    savedHours: "risparmiate",
    execHeroLabel: "Risparmio dimostrato",
    execPerDoors: "per {n} porte",
    execCompareTitle: "Senza Primo vs Con Primo",
    beforePrimo: "Senza Primo",
    afterPrimo: "Con Primo",
    cost: "Costo",
    time: "Tempo",
    costSavings: "Risparmio sui costi",
    timeSavings: "Risparmio di tempo",
    projectionPeriod: "Proiezione {period}",
    projectionAnnual: "Proiezione annualizzata",
    fleetTotalSavings: "Risparmio totale sulla flotta",
    weeklyLabel: "Settimanale",
    monthlyLabel: "Mensile",
    annualLabel: "Annuale",
    weeklyLower: "settimanale",
    monthlyLower: "mensile",
    annualLower: "annuale",
    perYear: "/ anno",
    doors_one: "porta",
    doors_other: "porte",
    sets_one: "set",
    sets_other: "set",
    hours_one: "ora",
    hours_other: "ore",
    logoAlt: "Primo",
    signatureAlt: "Firma",
  },
};

// ---------------------------------------------------------------
// REQUIRED_PDF_KEYS — derived from the pt-PT (most complete) dict.
// ---------------------------------------------------------------
export const REQUIRED_PDF_KEYS = Object.keys(pdfTranslations["pt-PT"]);

// ---------------------------------------------------------------
// Validation
// ---------------------------------------------------------------
export function validatePdfTranslations(lang) {
  if (!SUPPORTED_PDF_LANGS.includes(lang)) {
    return {
      ok: false,
      lang,
      missing: REQUIRED_PDF_KEYS.slice(),
      reason: "UNSUPPORTED_LANGUAGE",
    };
  }
  const dict = pdfTranslations[lang] || {};
  const missing = REQUIRED_PDF_KEYS.filter(
    (k) => typeof dict[k] !== "string" || dict[k].length === 0
  );
  return {
    ok: missing.length === 0,
    lang,
    missing,
    reason: missing.length === 0 ? null : "MISSING_KEYS",
  };
}

// ---------------------------------------------------------------
// makePdfT(lang) → tPdf(key, params)
// ---------------------------------------------------------------
// * NEVER falls back silently to English.
// * Logs `MISSING_PDF_TRANSLATION` when a key is missing.
// * Supports `{name}` interpolation.
// * Supports pluralised keys via `count` param: for `sets` it looks
//   up `sets_one` / `sets_other` using Intl.PluralRules.
// ---------------------------------------------------------------
function isDev() {
  try {
    return (
      typeof process !== "undefined" &&
      process.env &&
      process.env.NODE_ENV !== "production"
    );
  } catch (_) {
    return true;
  }
}

export function makePdfT(lang) {
  if (!SUPPORTED_PDF_LANGS.includes(lang)) {
    // Log loudly — we do NOT fall back to another language.
    // eslint-disable-next-line no-console
    console.error(
      `[pdfI18n] Unsupported language "${lang}". PDF cannot be rendered.`
    );
    throw new Error(`Unsupported PDF language: ${lang}`);
  }
  const dict = pdfTranslations[lang];
  const locale = langToLocale[lang];
  const pluralRules = new Intl.PluralRules(locale);

  return function tPdf(key, params = {}) {
    // Pluralised lookup: if `count` is present, look up `${key}_<category>`
    let value;
    if (params && typeof params.count === "number") {
      const category = pluralRules.select(params.count);
      value = dict[`${key}_${category}`] || dict[`${key}_other`];
    }
    if (typeof value !== "string") {
      value = dict[key];
    }
    if (typeof value !== "string") {
      // eslint-disable-next-line no-console
      console.error(
        `MISSING_PDF_TRANSLATION: language=${lang} key=${key}`
      );
      if (isDev()) {
        // Return a highly-visible sentinel so tests / eyes catch it.
        return `‹${key}›`;
      }
      return "";
    }
    // Interpolate placeholders {name}
    return value.replace(/\{(\w+)\}/g, (_, name) =>
      params && params[name] != null ? String(params[name]) : ""
    );
  };
}

// ---------------------------------------------------------------
// pdfFmt — locale-aware formatting helpers
// ---------------------------------------------------------------
export const pdfFmt = {
  locale(lang) {
    return langToLocale[lang] || "en-GB";
  },
  money(value, lang, opts = {}) {
    const locale = pdfFmt.locale(lang);
    const min = opts.minimumFractionDigits ?? 2;
    const max = opts.maximumFractionDigits ?? 2;
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    }).format(Number(value) || 0);
  },
  money0(value, lang) {
    return pdfFmt.money(value, lang, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  },
  number(value, lang, opts = {}) {
    return new Intl.NumberFormat(pdfFmt.locale(lang), opts).format(
      Number(value) || 0
    );
  },
  percent(value, lang, digits = 1) {
    // Value expected as a raw percentage (e.g. 61.5), not 0-1.
    const n = Number(value) || 0;
    const num = new Intl.NumberFormat(pdfFmt.locale(lang), {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(n);
    return `${num}\u00A0%`;
  },
  hours(value, lang, tPdf) {
    const n = Number(value) || 0;
    const num = new Intl.NumberFormat(pdfFmt.locale(lang), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
    // tPdf is optional; if provided use the localised abbreviation.
    // We use the noun/plural when tPdf is provided; otherwise fall back to
    // the language-neutral "h" abbreviation, which is understood everywhere.
    return `${num}\u00A0h`;
  },
  minAndHours(minutes, lang) {
    const locale = pdfFmt.locale(lang);
    const n = Number(minutes) || 0;
    const mins = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
    }).format(n);
    const hours = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n / 60);
    return `${mins}\u00A0min · ${hours}\u00A0h`;
  },
  date(d, lang) {
    const date = d instanceof Date ? d : new Date(d);
    return new Intl.DateTimeFormat(pdfFmt.locale(lang), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  },
};

// ---------------------------------------------------------------
// Convenience helper: label for a period code (weekly/monthly/annual)
// ---------------------------------------------------------------
export function periodLabelKey(period, style = "label") {
  // style === "label" → capitalised (Semanal); "lower" → lowercase (semanal)
  const suffix = style === "lower" ? "Lower" : "Label";
  if (period === "weekly") return `weekly${suffix}`;
  if (period === "monthly") return `monthly${suffix}`;
  return `annual${suffix}`;
}
